import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl } from '../../global/mediaQueryMixin';
import {
  AlignCenter,
  FlexBetween,
  FlexCenter,
  HideScrollbar,
  LineClamp,
  ShowVerticalScrollbar
} from '../../global/commonStyle';

interface IconProps {
  isActive?: boolean;
  marginLeft?: string;
}

export const CardWrapper = styled.form`
  display: flex;
  padding: var(--space-16);
  flex-direction: column;
  justify-content: center;
  gap: var(--space-16);
  border-radius: var(--border-radius-16);
  border: 0.5px solid var(--border-primary);
  background: var(--background-primary);
  margin: var(--space-20) var(--space-20) var(--space-0);
  ${mobile_xl(css`
    margin-block: var(--space-8) var(--space-12);
    gap: var(--space-12);
  `)}
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
`;

export const CardDescription = styled.div`
  ${FlexCenter}
  padding-left: var(--space-32);
  gap: var(--space-10);
  border: none;
  background: transparent;
  ${Typography.body_md}
  color: var(--text-primary);
  input {
    ${Typography.body_md}
    color: var(--text-primary);
    width: 100%;
    border: none;
    background: transparent;
    padding: var(--space-0);
    min-height: 40px;
    &:focus-visible {
      outline: none;
    }
  }
  textarea {
    ${Typography.body_md}
    color: var(--text-primary);
    width: 100%;
    border: none;
    background: transparent;
    padding: var(--space-0);
    min-height: 40px;
    font-family: 'Inter';
    resize: none;
    padding-left: var(--space-2);
    &:focus-visible {
      outline: none;
    }
    ${mobile_xl(css`
      min-height: 80px;
      padding-left: var(--space-0);
    `)}
  }
  ${mobile_xl(css`
    padding-left: var(--space-0);
  `)}
`;

export const CardBottomSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-12);
  justify-content: space-between;
  ${mobile_xl(css`
    padding-bottom: var(--space-12);
    border-bottom: 0.5px solid var(--border-primary);
  `)}
`;

export const TabDisplay = styled.div`
  ${AlignCenter}
  align-content: center;
  gap: var(--space-8);
  flex: 1 0 0;
  flex-wrap: wrap;
`;

export const TabSection = styled.div`
  ${AlignCenter}
  box-shadow: var(--border);
  border-radius: var(--border-radius-8);
  overflow: hidden;
  background-color: var(--background-primary);
  .icon-up {
    position: relative;
    left: -6px; /* or whatever offset looks best */
  }
  .status-icon-container {
    ${AlignCenter}
    position: relative;
  }
  .active {
    background-color: var(--neutral-secondary);
    box-shadow: var(--top-border), var(--bottom-border);
  }
`;
export const Chip = styled.div`
  ${AlignCenter}
  padding: var(--space-3) var(--space-8);
  gap: var(--space-6);
  border-radius: var(--border-radius-0);
  border-right: 0.5px solid var(--border-primary);
  cursor: pointer;
  span {
    ${Typography.body_md_medium}
    color: var(--text-primary);
    white-space: nowrap;
  }
  &.first-child {
    cursor: default;
    &:hover {
      background-color: transparent;
    }
  }
  &:hover {
    background-color: var(--neutral-secondary);
    box-shadow: var(--top-border), var(--bottom-border);
    .chip-close-icon {
      svg path {
        fill: var(--text-primary);
      }
    }
  }
  &:nth-child(4) {
    border-right: none;
    border-radius: var(--border-radius-0) var(--border-radius-8) var(--border-radius-8) var(--border-radius-0);
    &:hover {
      box-shadow: var(--top-border), var(--bottom-border), var(--right-border);
    }
  }
  .customer-avatar {
    border-radius: var(--border-radius-full);
    object-fit: cover;
  }
`;

export const FilteredText = styled.p`
  ${Typography.body_md_medium};
  margin: var(--space-0);
  color: var(--text-primary);
  ${mobile_xl(css`
    ${LineClamp(1)}
    text-overflow: ellipsis;
    word-break: break-all;
  `)}
`;

export const TitleBar = styled.div`
  display: flex;
  gap: var(--space-8);
  ${mobile_xl(css`
    flex-direction: column;
    gap: var(--space-14);
  `)};
`;

export const IconDiv = styled.div<IconProps>`
  ${AlignCenter}
  margin-left: ${({ marginLeft }) => marginLeft || 'var(--space-0)'};
  &.chip-close-icon {
    padding-block: var(--space-2);
  }
  .avatar {
    span {
      color: var(--text-white);
      ${Typography.body_xs}
      font-size: 8px;
    }
  }
`;

export const DropDiv = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--space-12) var(--space-12) var(--space-0) var(--space-12);
  gap: var(--space-12);
  min-height: 281px;
`;

export const TabButtonDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-4);
  align-self: stretch;
`;
export const TabItem = styled.div<{ isActive?: boolean }>`
  ${FlexCenter}
  padding: var(--space-3) var(--space-8);
  gap: var(--space-6);
  border-radius: var(--border-radius-6);
  background: ${(props) => (props.isActive ? 'var(--background-muted)' : 'transparent')};
  color: ${(props) => (props.isActive ? 'var(--text-primary)' : 'var(--text-secondary)')};
  ${Typography.body_md_medium}
  cursor: pointer;

  &:hover {
    background: var(--background-muted);
    color: var(--text-primary);
  }
`;

export const ContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-8);
`;

export const ColorsDiv = styled.div`
  ${AlignCenter}
  gap: var(--space-8);
`;

export const Circles = styled.div`
  ${FlexBetween}
  cursor: pointer;
`;

export const ColorDots = styled.div`
  ${AlignCenter}
  gap: var(--space-8);
`;

export const Span = styled.div`
  width: 20px;
  height: 20px;
  border-radius: var(--border-radius-full);
  margin: var(--space-2);
  ${FlexCenter}
  background-color: ${(props) => props.color};
  &:hover {
    margin: var(--space-0);
    width: 24px;
    height: 24px;
  }
`;

export const IconsDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  width: 100%;
  .EmojiPickerReact {
    background-color: var(--background-primary);
  }
  .epr_-99lr9q {
    background-color: var(--background-primary);
  }
  .epr_-6npj90 {
    border-style: none;
    width: 100% !important;
  }
  .epr_-kg0voo {
    display: none;
  }
  .epr-body {
    width: 100%;
  }
  .EmojiPickerReact li.epr-emoji-category > .epr-emoji-category-content {
    margin: var(--space-0);
  }
  .EmojiPickerReact .epr-skin-tones {
    display: none;
  }
  .EmojiPickerReact .epr-category-nav {
    display: none;
  }
  .epr_-8ygbw8 {
    padding: var(--space-0) !important;
  }
  .epr-search-container .epr_-2zpaw9 {
    ${FlexCenter}
    height: 26px;
    padding: var(--space-3) var(--space-10);
    width: 100%;
    gap: var(--space-10);
    border-radius: var(--border-radius-4);
    border: 0.5px solid var(--border-primary);
    background: var(--background-tertiary);
    ${Typography.body_md}
    color: var(--text-primary);
    :placeholder {
      color: var(--text-secondary);
    }
    &:focus-visible {
      outline: none;
    }
  }
  .epr-search-container {
    ${AlignCenter}
    padding-block: var(--space-3);
    gap: var(--space-64);
  }

  .EmojiPickerReact:not(.epr-search-active) .epr-category-nav > button.epr-cat-btn.epr-active,
  .EmojiPickerReact li.epr-emoji-category > .epr-emoji-category-label {
    padding-top: var(--space-8) !important;
  }
  .epr_gd8l4g {
    padding: var(--space-0);
  }
  .EmojiPickerReact button.epr-emoji {
    max-height: 28px !important;
    max-width: 28px !important;
    height: 28px !important;
    width: 28px !important;
    border-radius: var(--border-radius-6);
    &:hover {
      background-color: var(--neutral-secondary);
    }
  }
  .EmojiPickerReact button.epr-emoji .epr-emoji-img {
    padding: var(--space-6) !important;
    max-height: 28px !important;
    max-width: 28px !important;
    min-height: 28px !important;
    min-width: 28px !important;
  }
  .EmojiPickerReact li.epr-emoji-category > .epr-emoji-category-content {
    gap: var(--space-0) !important;
  }
`;

export const SearchDiv = styled.div`
  ${AlignCenter}
  padding-block: var(--space-2);
  gap: var(--space-64);
  .input {
    width: 100%;
  }
`;

export const IconsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  overflow: auto;
  max-height: 172px;
  ${ShowVerticalScrollbar}
`;

export const Icon = styled.div<IconProps>`
  ${FlexCenter}
  border-radius: var(--border-radius-6);
  cursor: pointer;
  width: 28px;
  height: 28px;
  background: ${({ isActive }) => (isActive ? 'var(--background-secondary)' : 'transparent')};
  span {
    width: 16px;
    height: 16px;
  }
  &:hover {
    background: var(--neutral-secondary) !important;
  }
`;

export const AddIconDiv = styled.div`
  ${AlignCenter}
`;

export const UploadImageDiv = styled.div`
  ${FlexCenter}
  padding-inline: var(--space-20);
  flex-direction: column;
  gap: var(--space-16);
  min-height: 240px;
`;

export const ImageContainer = styled.div`
  ${FlexCenter}
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-8);
  border: 0.5px solid var(--border-primary);
  overflow: hidden;
`;

export const UploadTextDiv = styled.div`
  ${AlignCenter}
  flex-direction: column;
  gap: var(--space-12);
  p {
    margin: var(--space-0);
    ${Typography.body_md};
    color: var(--text-secondary);
    text-align: center;
  }
`;

export const UploadButton = styled.div`
  cursor: pointer;
  ${FlexCenter}
  padding: var(--space-5) var(--space-10);
  gap: var(--space-4);
  border-radius: var(--border-radius-8);
  border: 0.5px solid var(--border-primary);
  background: var(--background-primary);
  ${Typography.body_sm};
  color: var(--text-primary);
`;

export const SVGIconDiv = styled.div`
  width: max-content;
  padding: var(--space-8);
  ${FlexCenter}
  box-shadow: var(--border);
  background: var(--background-primary);
  border-radius: var(--border-radius-6);
  cursor: pointer;
  &.views {
    padding: var(--space-5);
    ${mobile_xl(css`
      padding: var(--space-6);
      svg {
        width: 20px;
        height: 20px;
      }
    `)}
  }
`;

export const EmptyText = styled.div`
  ${FlexCenter}
  padding-block: var(--space-64) var(--space-80);
`;

export const Calender = styled.div`
  width: 100%;
  .icon {
    width: 14px;
    height: 14px;
    ${mobile_xl(css`
      width: 16px;
      height: 16px;
    `)}
  }
  .text-center {
    margin: var(--space-0);
    ${Typography.body_md}
    color: var(--text-primary);
  }
  ${mobile_xl(css`
    width: unset;
    padding-block: var(--space-8);
    padding-inline: var(--space-10);
  `)}

  .react-calendar {
    padding: var(--space-4) var(--space-10);
    max-width: 100%;
    ${Typography.body_xs}
    text-align: center;
    letter-spacing: 0.1px;
    color: var(--text-secondary);
    ${mobile_xl(css`
      padding-inline: var(--space-4);
    `)}
  }
  .react-calendar__month-view__days {
    display: grid !important;
    grid-template-columns: repeat(7, 1fr);
    row-gap: var(--space-2);
  }
  .react-calendar--doubleView {
    width: 700px;
  }
  .react-calendar__month-view {
    flex-grow: unset;
  }
  /* Today tile: hover/focus background and rounding */
  .react-calendar__tile--now:enabled:hover,
  .react-calendar__tile--now:enabled:focus {
    border-radius: var(--border-radius-8);
    background-color: var(--text-secondary);
  }
  .react-calendar--doubleView .react-calendar__viewContainer {
    display: flex;
    margin: -0.5em;
  }
  .react-calendar--doubleView .react-calendar__viewContainer > * {
    width: 50%;
    margin: var(--space-8);
  }
  .react-calendar,
  .react-calendar *,
  .react-calendar *:before,
  .react-calendar *:after {
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    text-decoration: none;
  }
  .react-calendar button {
    margin: var(--space-0);
    border: 0;
    outline: none;
  }
  .react-calendar button:enabled:hover {
    cursor: pointer;
  }
  .react-calendar__navigation {
    display: flex;
    margin-bottom: var(--space-8);
    padding-block: var(--space-4);
    ${mobile_xl(css`
      margin-bottom: var(--space-16);
      gap: var(--space-12);
    `)}
  }
  .react-calendar__navigation button {
    ${Typography.body_lg_semibold}
    font-family: 'Inter';
    background: none;
    height: 20px;
    padding: var(--space-0);
    color: var(--text-primary);
    width: 20px;
    ${FlexCenter}
    &:hover {
      background-color: var(--neutral-secondary);
      border-radius: var(--border-radius-4);
    }
    ${mobile_xl(css`
      padding: var(--space-0);
      width: 26px;
      height: 26px;
      svg {
        width: 16px;
        height: 16px;
      }
    `)}
  }

  .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: uppercase;
    margin-bottom: var(--space-12);
    ${FlexBetween}
    ${Typography.body_xs}
    ${mobile_xl(css`
      margin-bottom: var(--space-12);
    `)}
  }
  .react-calendar__month-view__weekdays__weekday {
    ${mobile_xl(css`
      padding: var(--space-0);
    `)}
  }
  .react-calendar__month-view__weekNumbers .react-calendar__tile {
    ${FlexCenter}
    ${Typography.body_xs}
  }

  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    padding: var(--space-2);
  }
  .react-calendar__year-view__months__month {
    overflow: auto;
  }
  .react-calendar__tile {
    height: 32px;
    max-width: 100%;
    padding-block: var(--space-2);
    background: none;
    text-align: center;
    ${Typography.body_md}
    border-radius: var(--border-radius-8);
    color: var(--text-primary);
  }
  /* Generic hover/focus: non-range tiles use neutral background */
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background: var(--neutral-secondary) !important;
    color: var(--text-primary) !important;
  }
  /* Hover/focus for tiles that are part of a selected range */
  .react-calendar__tile--range:enabled:hover,
  .react-calendar__tile--range:enabled:focus {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
  }
  /* Hover/focus for tiles that are part of a selected range (year view: keep unset) */
  .react-calendar__year-view__months__month.react-calendar__tile--range:enabled:hover,
  .react-calendar__year-view__months__month.react-calendar__tile--range:enabled:focus {
    background: var(--neutral-secondary) !important;
    color: var(--text-primary) !important;
    border-radius: var(--border-radius-8) !important;
  }
  .react-calendar__tile--now {
    border-radius: var(--border-radius-8);
  }
  .react-calendar__tile--active:enabled:hover {
    background: var(--brand-primary);
    color: var(--text-white);
  }

  /* Active tile (clicked/selected single date): primary background */
  .react-calendar__tile--active:enabled {
    background: var(--text-primary);
    color: var(--text-white);
    border-radius: var(--border-radius-8);
    ${mobile_xl(css`
      flex: unset !important;
      max-width: 44px;
      width: 100%;
    `)}
  }

  /* Active tile base shape (ensures consistent rounding) */
  .react-calendar__tile--active {
    border-radius: var(--border-radius-8);
    background: var(--text-primary);
    color: var(--text-white);
  }

  /* Range selection styling - Continuous rounded rectangles */
  .react-calendar__tile--rangeStart {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: var(--border-radius-8) 0 0 var(--border-radius-8) !important;
    position: relative !important;
  }

  .react-calendar__tile--rangeEnd {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: 0 var(--border-radius-8) var(--border-radius-8) 0 !important;
    position: relative !important;
  }

  .react-calendar__tile--rangeStart.react-calendar__tile--rangeEnd {
    border-radius: var(--border-radius-8) !important;
  }

  /* Range container (applies while dragging/selected range, used as base) */
  .react-calendar__tile--range {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    position: relative !important;
  }

  /* Year view: never show range styling on month tiles */
  .react-calendar__year-view__months__month.react-calendar__tile--range {
    background-color: unset !important;
    color: var(--text-primary) !important;
  }
  /* Year view: also clear when tile is active + in range */
  .react-calendar__tile.react-calendar__tile--active.react-calendar__tile--range
    .react-calendar__year-view__months__month {
    background-color: unset !important;
    color: var(--text-primary) !important;
  }
  /* Year view: clear mid-range fill as well */
  .react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(
      .react-calendar__tile--rangeEnd
    ).react-calendar__year-view__months__month {
    background-color: unset !important;
  }
  .react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(
      .react-calendar__tile--rangeEnd
    ).react-calendar__year-view__months__month {
    background-color: unset !important;
  }
  /* Mid-range tiles (between start and end) in month view */
  .react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(.react-calendar__tile--rangeEnd) {
    background: var(--neutral-secondary) !important;
    color: var(--text-primary) !important;
    border-radius: 0 !important;
  }

  /* Week-based rounding for range selection - Target days within the month view */
  .react-calendar__month-view__days .react-calendar__tile--range:nth-child(7n) {
    border-radius: 0 var(--border-radius-8) var(--border-radius-8) 0 !important;
  }

  .react-calendar__month-view__days .react-calendar__tile--range:nth-child(7n-6) {
    border-radius: var(--border-radius-8) 0 0 var(--border-radius-8) !important;
  }

  /* If rangeStart lands on last column (7n), round RIGHT (not left) */
  .react-calendar__month-view__days .react-calendar__tile--rangeStart:nth-child(7n) {
    border-radius: var(--border-radius-8) !important;
  }

  /* If rangeEnd lands on first column (7n-6), round LEFT (not right) */
  .react-calendar__month-view__days .react-calendar__tile--rangeEnd:nth-child(7n-6) {
    border-radius: var(--border-radius-8) !important;
  }

  /* Override the default active tile styling for range selection */
  .react-calendar__tile--active:not(.react-calendar__tile--rangeStart):not(.react-calendar__tile--rangeEnd):not(
      .react-calendar__tile--range
    ) {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: var(--border-radius-8) !important;
  }

  .react-calendar__year-view__months {
    row-gap: var(--space-3);
  }
  /* More precise hover: base tile hover in any view */
  .react-calendar__tile--hover {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
  }
  /* Month view hover: default to primary background for hovered tiles */
  .react-calendar__month-view__days .react-calendar__tile--hover {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: 0 !important;
    position: relative !important;
  }

  /* Hover: when a hovered tile starts a sequence (left-rounded) */
  .react-calendar__month-view__days
    .react-calendar__tile:not(.react-calendar__tile--hover):not(.react-calendar__tile--range)
    + .react-calendar__tile--hover {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: var(--border-radius-8) 0 0 var(--border-radius-8) !important;
  }
  /* Hover: right-most day of week still stays fully rounded */
  .react-calendar__month-view__days
    .react-calendar__tile:not(.react-calendar__tile--hover):not(.react-calendar__tile--range)
    + .react-calendar__tile--hover.react-calendar__tile--hover:nth-child(7n) {
    border-radius: var(--border-radius-8) !important;
  }

  /* Hover: when a hovered tile ends a sequence (right-rounded) */
  .react-calendar__month-view__days
    .react-calendar__tile.react-calendar__tile--hover:has(
      + .react-calendar__tile:not(.react-calendar__tile--hover):not(.react-calendar__tile--range)
    ) {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: 0 var(--border-radius-8) var(--border-radius-8) 0 !important;
  }

  /* Hover: left-most day of week still stays fully rounded */
  .react-calendar__month-view__days
    .react-calendar__tile.react-calendar__tile--hover:has(
      + .react-calendar__tile:not(.react-calendar__tile--hover):not(.react-calendar__tile--range)
    ).react-calendar__tile--hover.react-calendar__tile--hover:nth-child(7n-6) {
    border-radius: var(--border-radius-8) !important;
  }

  /* Hover: when hovered tile is last in row, force right rounding */
  .react-calendar__month-view__days .react-calendar__tile .react-calendar__tile--hover:nth-child(7n) {
    border-top-right-radius: var(--border-radius-8) !important;
    border-bottom-right-radius: var(--border-radius-8) !important;
  }

  /* Hover: middle tiles (between start/end) use neutral background */
  .react-calendar__month-view__days .react-calendar__tile--hover:not(:first-child):not(:last-child) {
    background: var(--neutral-secondary) !important;
    color: var(--text-primary) !important;
  }

  /* Single-day hovered segment (prev and next are neither hovered nor in selected range) */
  .react-calendar__month-view__days
    .react-calendar__tile:not(.react-calendar__tile--hover):not(.react-calendar__tile--range)
    + .react-calendar__tile--hover:has(
      + .react-calendar__tile:not(.react-calendar__tile--hover):not(.react-calendar__tile--range)
    ) {
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
    border-radius: var(--border-radius-8) !important;
  }

  /* Week-edge rounding for hovered range */
  .react-calendar__month-view__days .react-calendar__tile--hover:nth-child(7n) {
    border-radius: 0 var(--border-radius-8) var(--border-radius-8) 0 !important;
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
  }
  .react-calendar__month-view__days .react-calendar__tile--hover:nth-child(7n-6) {
    border-radius: var(--border-radius-8) 0 0 var(--border-radius-8) !important;
    background: var(--text-primary) !important;
    color: var(--text-white) !important;
  }
  /* Hover/focus for tiles that are part of a selected range (year view: mid-range only) */
  .react-calendar__year-view__months__month.react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(
      .react-calendar__tile--rangeEnd
    ):enabled:hover,
  .react-calendar__year-view__months__month.react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(
      .react-calendar__tile--rangeEnd
    ):enabled:focus {
    background: var(--neutral-secondary) !important;
    color: var(--text-primary) !important;
    border-radius: var(--border-radius-8) !important;
  }

  /* Year view: mid-range tiles hover/focus uses neutral background */
  .react-calendar__year-view__months__month.react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(
      .react-calendar__tile--rangeEnd
    ):enabled:hover,
  .react-calendar__year-view__months__month.react-calendar__tile--range:not(.react-calendar__tile--rangeStart):not(
      .react-calendar__tile--rangeEnd
    ):enabled:focus {
    background: var(--neutral-secondary) !important;
    color: var(--text-primary) !important;
    border-radius: var(--border-radius-8) !important;
  }
  /* Year view (months): suppress hover visuals during range selection */
  .react-calendar__year-view__months__month.react-calendar__tile--hover,
  .react-calendar__year-view__months__month.react-calendar__tile--rangeStart.react-calendar__tile--hover,
  .react-calendar__year-view__months__month.react-calendar__tile--rangeEnd.react-calendar__tile--hover,
  .react-calendar__year-view__months__month.react-calendar__tile--range.react-calendar__tile--hover {
    background: unset !important;
    color: var(--text-primary) !important;
  }
`;

export const ResponsiveButtonSection = styled.div`
  ${FlexBetween}
`;

export const TaskTitle = styled.textarea`
  width: 100%;
  font-family: unset;
  resize: none;
  overflow: hidden;
  min-height: 26px;
  line-height: 1.5;
  ${Typography.heading_lg}
  color: var(--text-primary);
  margin: var(--space-0);
  border: none;
  background-color: var(--background-primary);
  border-radius: var(--border-radius-4);
  padding: var(--space-0);
  ${HideScrollbar}
  ::placeholder {
    ${Typography.heading_lg}
    color: var(--text-placeholder);
  }
  &:focus {
    outline: none;
  }
`;
